import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import {
  ShieldQuestion,
  Search,
  CheckCircle2,
  XCircle,
  Clock,
  FlaskConical,
} from "lucide-react";
import { toast } from "sonner";

const VERDICT_STYLES = {
  "sent-admin": {
    label: "Admin — will receive",
    className: "bg-emerald-50 text-emerald-700 border-emerald-200",
    icon: CheckCircle2,
  },
  "sent-tenant-user": {
    label: "Tenant user — will receive",
    className: "bg-emerald-50 text-emerald-700 border-emerald-200",
    icon: CheckCircle2,
  },
  "sent-allowlisted": {
    label: "Allowlisted — will receive",
    className: "bg-emerald-50 text-emerald-700 border-emerald-200",
    icon: CheckCircle2,
  },
  "blocked-master": {
    label: "Master-only — BLOCKED",
    className: "bg-[#FF3B30]/10 text-[#FF3B30] border-[#FF3B30]/20",
    icon: XCircle,
  },
};

const OVERALL_STYLES = {
  kept: {
    label: "Would receive mail",
    className: "bg-emerald-50 text-emerald-700 border-emerald-300",
    icon: CheckCircle2,
  },
  dropped: {
    label: "Blocked — no mail sent",
    className: "bg-[#FF3B30]/10 text-[#FF3B30] border-[#FF3B30]/40",
    icon: XCircle,
  },
};

function relTime(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.max(0, Math.floor(ms / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function classifyEventDetail(detail) {
  const d = String(detail || "");
  if (d.startsWith("skipped-master-only-")) return { label: "Master-guard blocked", tone: "amber" };
  if (d.startsWith("skipped-bot-")) return { label: "Bot-guard skipped", tone: "amber" };
  if (d.startsWith("mock-")) return { label: "Mock (dev)", tone: "zinc" };
  return { label: "Real send", tone: "emerald" };
}

export default function EmailBlockAuditCard() {
  const [email, setEmail] = useState("");
  const [lookup, setLookup] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  async function runAudit(e) {
    e?.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) {
      toast.error("Please enter an email address");
      return;
    }
    setLoading(true);
    setHasSearched(true);
    try {
      const [lookupRes, eventsRes] = await Promise.all([
        api.get("/admin/email-block-audit/lookup", { params: { email: trimmed } }),
        api.get("/admin/email-events", { params: { to: trimmed, limit: 20 } }),
      ]);
      setLookup(lookupRes.data);
      setEvents(Array.isArray(eventsRes.data) ? eventsRes.data : []);
    } catch (err) {
      toast.error(formatApiError(err));
      setLookup(null);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }

  const overall = lookup ? OVERALL_STYLES[lookup.overall_verdict] : null;
  const OverallIcon = overall?.icon;

  return (
    <section
      className="border border-zinc-200 rounded-sm bg-white p-6"
      data-testid="email-block-audit-card"
    >
      <div className="mb-4">
        <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">
          <ShieldQuestion className="h-3.5 w-3.5" /> Email block audit
        </div>
        <h2 className="font-heading text-lg font-bold text-zinc-950 mt-1">
          Check whether a given email would receive mail right now.
        </h2>
        <p className="text-sm text-zinc-500 mt-1">
          Runs the live master-only guard against any address (case-insensitive)
          and shows the last 20 send attempts for it.
        </p>
      </div>

      <form onSubmit={runAudit} className="flex flex-col sm:flex-row gap-2 mb-4">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="jomackmurdie@hotmail.co.uk"
          data-testid="email-block-audit-input"
          className="flex-1 px-3 py-2 text-sm border border-zinc-300 rounded-sm focus:outline-none focus:border-zinc-950 transition-colors"
        />
        <button
          type="submit"
          disabled={loading || !email.trim()}
          data-testid="email-block-audit-submit"
          className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-zinc-950 text-white text-xs uppercase tracking-[0.15em] font-bold rounded-sm hover:bg-zinc-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Search className="h-3.5 w-3.5" />
          {loading ? "Checking…" : "Check"}
        </button>
      </form>

      {/* Always show the current hard allowlist so operators can see
          which addresses are exempt from the strict guard. */}
      {hasSearched && !loading && lookup && Array.isArray(lookup.hard_allowlist) && lookup.hard_allowlist.length > 0 && (
        <div
          className="mb-6 px-3 py-2 border border-zinc-200 bg-zinc-50 rounded-sm text-xs text-zinc-600"
          data-testid="email-block-audit-allowlist"
        >
          <span className="font-bold uppercase tracking-widest text-zinc-500">
            Hard allowlist:
          </span>{" "}
          {lookup.hard_allowlist.map((a, i) => (
            <span key={a} className="font-mono text-zinc-800">
              {a}
              {i < lookup.hard_allowlist.length - 1 ? ", " : ""}
            </span>
          ))}
          <div className="text-[11px] text-zinc-500 mt-1">
            Every other address — including all unknown external contacts — is
            dropped by the guard unless it belongs to a real pub/client user.
          </div>
        </div>
      )}

      {hasSearched && !loading && lookup && (
        <div className="space-y-6" data-testid="email-block-audit-result">
          {/* Overall verdict banner */}
          {overall && (
            <div
              className={`flex items-center gap-3 px-4 py-3 border rounded-sm ${overall.className}`}
              data-testid="email-block-audit-overall-verdict"
            >
              {OverallIcon && <OverallIcon className="h-5 w-5 shrink-0" />}
              <div className="flex-1">
                <div className="text-sm font-bold">{overall.label}</div>
                <div className="text-xs opacity-80 mt-0.5">
                  {lookup.match_count} record{lookup.match_count === 1 ? "" : "s"} found for{" "}
                  <span className="font-mono">{lookup.input}</span>
                  {lookup.in_hard_allowlist && (
                    <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[9px] font-bold uppercase tracking-widest bg-white/60 border border-current">
                      Hard-allowlist hit
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Per-doc verdict rows */}
          {lookup.rows.length > 0 && (
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-2">
                Registered user record{lookup.rows.length === 1 ? "" : "s"}
              </div>
              <ul className="divide-y divide-zinc-100 border border-zinc-100 rounded-sm">
                {lookup.rows.map((row) => {
                  const style = VERDICT_STYLES[row.verdict] || {
                    label: row.verdict,
                    className: "bg-zinc-50 text-zinc-700 border-zinc-200",
                    icon: ShieldQuestion,
                  };
                  const RowIcon = style.icon;
                  return (
                    <li
                      key={row.id}
                      className="p-4"
                      data-testid={`email-block-audit-row-${row.id}`}
                    >
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="min-w-0">
                          <div className="text-sm font-bold text-zinc-950">
                            {row.name || <span className="text-zinc-400">(no name)</span>}
                          </div>
                          <div className="text-xs font-mono text-zinc-500 break-all">
                            {row.email}
                          </div>
                        </div>
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-1 rounded-sm text-[10px] font-bold uppercase tracking-widest border ${style.className}`}
                        >
                          <RowIcon className="h-3 w-3" />
                          {style.label}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3 text-[11px]">
                        <FieldPill label="Role" value={row.role || "user"} />
                        <FieldPill label="Editor" value={row.is_editor ? "yes" : "no"} />
                        <FieldPill label="Pub" value={row.pub_slug || row.pub_id || "—"} />
                        <FieldPill label="Client" value={row.client_slug || row.client_id || "—"} />
                      </div>
                      <div className="text-xs text-zinc-500 mt-3 italic">
                        {row.reason}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {lookup.match_count === 0 && (
            <div className="px-4 py-3 border border-zinc-200 bg-zinc-50 text-sm text-zinc-600 rounded-sm">
              No matching record in <span className="font-mono">db.users</span>.{" "}
              {lookup.in_hard_allowlist ? (
                <>
                  This address is on the <strong>hard allowlist</strong> — mail will be sent.
                </>
              ) : (
                <>
                  Unknown external addresses are <strong>dropped</strong> unless present
                  in the hard allowlist (see above).
                </>
              )}
            </div>
          )}

          {/* Recent events */}
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-2 flex items-center gap-1">
              <Clock className="h-3 w-3" /> Recent send history
            </div>
            {events.length === 0 ? (
              <div className="text-sm text-zinc-500 italic">
                No send events logged for this address yet.
              </div>
            ) : (
              <ul
                className="divide-y divide-zinc-100 border border-zinc-100 rounded-sm"
                data-testid="email-block-audit-events"
              >
                {events.map((ev) => {
                  const cls = classifyEventDetail(ev.detail);
                  const toneCls =
                    cls.tone === "emerald"
                      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                      : cls.tone === "amber"
                      ? "bg-amber-50 text-amber-700 border-amber-200"
                      : "bg-zinc-50 text-zinc-600 border-zinc-200";
                  return (
                    <li key={ev.id} className="p-3 text-xs">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold uppercase tracking-[0.15em] text-zinc-950">
                          {ev.kind}
                        </span>
                        <span
                          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[9px] font-bold uppercase tracking-widest border ${toneCls}`}
                        >
                          {cls.label}
                        </span>
                        {ev.mocked && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[9px] font-bold uppercase tracking-widest bg-amber-50 text-amber-700 border border-amber-200">
                            <FlaskConical className="h-2.5 w-2.5" /> Mock
                          </span>
                        )}
                        <span className="text-zinc-400 ml-auto">
                          {relTime(ev.created_at)}
                        </span>
                      </div>
                      <div className="font-mono text-zinc-500 mt-1 break-all">
                        {ev.detail}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

function FieldPill({ label, value }) {
  return (
    <div className="border border-zinc-100 bg-zinc-50 px-2 py-1 rounded-sm">
      <div className="text-[9px] uppercase tracking-widest text-zinc-400 font-bold">
        {label}
      </div>
      <div className="text-zinc-800 truncate" title={String(value)}>
        {value}
      </div>
    </div>
  );
}
